import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Charger, QueueAccessCheck, QueueDetail, User } from '../types';
import { chargerService } from '../services/chargerService';
import { queueAccessService } from '../services/queueAccessService';
import { authService } from '../services/authService';
import { useApp } from '../contexts/AppContext';
import { FindUserModal } from './FindUserModal';
import { UpdateChargingModal } from './UpdateChargingModal';
import { useQueueJoinFlow } from '../hooks/useQueueJoinFlow';
import { 
  Zap, 
  Users, 
  WifiOff, 
  Wifi, 
  Play, 
  Clock,
  X,
  Calendar,
  Circle,
  UserSearch,
  Car
} from 'lucide-react';

interface KioskImpersonationData {
  token: string;
  user: User;
}

// Extend Window interface for better type safety
declare global {
  interface Window {
    __kioskImpersonationData?: KioskImpersonationData;
  }
}

interface ChargerCardProps {
  charger: Charger;
  onAction: () => void;
}

export function ChargerCard({ charger, onAction }: ChargerCardProps) {
  const { joinQueue, modals: queueJoinModals } = useQueueJoinFlow();
  const { t } = useTranslation();
  const { state } = useApp();
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [showFindUserModal, setShowFindUserModal] = useState(false);
  const [showQueueUsersModal, setShowQueueUsersModal] = useState(false);
  const [startChargingError, setStartChargingError] = useState<string | null>(null);
  const [extendChargingError, setExtendChargingError] = useState<string | null>(null);
  const [kioskActionType, setKioskActionType] = useState<'charging' | 'queue' | null>(null);
  const [showForceStartModal, setShowForceStartModal] = useState(false);

  const getStatusInfo = () => {
    if (charger.status === 'idle') {
      return {
        status: 'idle',
        label: t('home.charger.status.available'),
        color: 'var(--color-available)',
        bgColor: 'var(--color-success-bg)',
        icon: Zap
      };
    }
    
    if (charger.status === 'charging') {
      if (charger.isSessionOverdue) {
        return {
          status: 'overdue',
          label: t('home.charger.overdue'),
          color: 'var(--color-error)',
          bgColor: 'var(--color-error-bg)',
          icon: Clock
        };
      }
      return {
        status: 'charging',
        label: t('home.charger.charging'),
        color: 'var(--color-charging)',
        bgColor: 'var(--color-primary-bg)',
        icon: Zap
      };
    }
    
    if (charger.status === 'awaitingNext') {
      return {
        status: 'awaitingNext',
        label: t('home.charger.awaitingNext'),
        color: 'var(--color-queue)',
        bgColor: 'var(--color-warning-bg)',
        icon: Clock
      };
    }
    
    return {
      status: 'awaiting',
      label: 'Awaiting',
      color: 'var(--color-queue)',
      bgColor: 'var(--color-warning-bg)',
      icon: Clock
    };
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;
  const queueLength = charger.queueLength;
  const isUserInQueue = charger.isUserInQueue;
  const isUserCharging = charger.isUserCharging || false;
  const isUserChargingAnywhere = charger.isUserChargingAnywhere || false;
  
  // Check if user is the first real user in queue (for awaitingNext status)
  const isFirstRealUserInQueue = charger.isUserInQueue && charger.userQueuePosition === 1;
  
  // NEW LOGIC: User can start charging if:
  // 1. Charger is online 
  // 2. User is approved (status 1)
  // 3. Charger is idle (nobody else charging) OR charger is awaitingNext and user is first real user in queue
  // 4. User is NOT charging anywhere else
  const canStartCharging = charger.online && 
                           (charger.status === 'idle' || 
                            (charger.status === 'awaitingNext' && isFirstRealUserInQueue)) && 
                           state.user?.status === 1 &&
                           !isUserChargingAnywhere;

  // Force start detection helpers
  const isUserNextInQueue = charger.isUserInQueue && charger.userQueuePosition === 1;
  const hasActiveSession = charger.status === 'charging';
  const canForceStart = charger.online && 
                        state.user?.status === 1 && 
                        !isUserChargingAnywhere && 
                        isUserNextInQueue && 
                        hasActiveSession;

  // NEW LOGIC: User can join queue if:
  // 1. User is NOT charging anywhere AND
  // 2. User is NOT already in any queue AND
  // 3. Backend allows it (charger.canJoinQueue already considers these conditions)
  const canJoinQueue = !isUserChargingAnywhere && 
                       !isUserInQueue && 
                       charger.canJoinQueue;

  // UPDATED LOGIC: Show "Cancel Queue" if user is in THIS queue
  const showCancelQueue = isUserInQueue;

  // NEW LOGIC: Show "Update Charging" if user is charging on THIS slot
  const showUpdateCharging = isUserCharging;

  const handleStartCharging = async (minutes: number) => {
    // Check if we're in kiosk mode and have an impersonation token
    const isKiosk = authService.isKioskUser();
    const impersonationData = window.__kioskImpersonationData;
    
    try {
      setIsLoading(true);
      setStartChargingError(null); // Clear any previous errors
      
      if (isKiosk && impersonationData) {
        console.log('Kiosk mode: Starting charging for impersonated user:', impersonationData.user.username);
        
        // Execute the charging operation with impersonated token
        await authService.executeAsImpersonatedUser(impersonationData.token, async () => {
          return chargerService.startCharging(charger.id, { estimatedMinutes: minutes });
        });
        
        // Clean up impersonation data after successful operation
        delete window.__kioskImpersonationData;
        console.log('Kiosk mode: Charging started successfully, cleaned up impersonation data');
      } else {
        // Normal user flow
        await chargerService.startCharging(charger.id, { estimatedMinutes: minutes });
      }
      
      setShowStartModal(false);
      onAction();
    } catch (error: any) {
      console.error('Failed to start charging:', error);
      
      // Show user-friendly localized error message
      let errorMessage = t('errors.generic');
      
      // First priority: Use the server's validation error message if available
      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;
        console.log('Server message:', serverMessage);
        // Check if the server message is a localization key (our new approach)
        if (serverMessage === 'chargingTimeExceedsMaximum') {
          errorMessage = t('errors.chargingTimeExceedsMaximum');
        } else if (serverMessage === 'extensionTimeExceedsMaximum') {
          errorMessage = t('errors.extensionTimeExceedsMaximum');
        } else if (serverMessage === 'invalidChargingDuration') {
          errorMessage = t('errors.invalidChargingDuration');
        } else {
          // Use the server message directly (it's a regular error message)
          errorMessage = serverMessage;
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        // Fallback to error.message if no structured response
        if (error.message.includes('active charging session') || error.message.includes('הפעלת טעינה פעילה') || error.message.includes('активной сессии зарядки')) {
          errorMessage = t('errors.startChargingActiveSession');
        } else {
          errorMessage = error.message;
        }
      }
      
      console.error('Error response:', error.response?.data);
      console.error('Error response keys:', Object.keys(error.response?.data || {}));
      // Set error state to display in modal
      setStartChargingError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Force start handler - ends current session and starts new one
  const handleForceStart = async (minutes: number) => {
    try {
      setIsLoading(true);
      setStartChargingError(null);
      
      await chargerService.forceStartCharging(charger.id, { estimatedMinutes: minutes });
      
      setShowForceStartModal(false);
      if (onAction) {
        onAction();
      }
    } catch (error: any) {
      console.error('Failed to force start charging:', error);
      
      let errorMessage = t('errors.genericError');
      if (error.message) {
        if (error.message.includes('404')) {
          errorMessage = t('errors.chargerNotFound');
        } else if (error.message.includes('already charging')) {
          errorMessage = t('errors.alreadyCharging');
        } else if (error.message.includes('not in queue') || error.message.includes('first in queue')) {
          errorMessage = t('errors.notFirstInQueue');
        } else {
          errorMessage = error.message;
        }
      }
      
      setStartChargingError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinQueue = () => {
    if (authService.isKioskUser()) {
      setKioskActionType('queue');
      setShowFindUserModal(true);
      return;
    }
    setShowQueueModal(true);
  };

  const handleConfirmJoinQueue = async (shift: number, parkingSlot?: string) => {
    // Check if we're in kiosk mode and have an impersonation token
    const isKiosk = authService.isKioskUser();
    const impersonationData = window.__kioskImpersonationData;
    
    if (isKiosk && impersonationData) {
      console.log('Kiosk mode: Joining queue for impersonated user:', impersonationData.user.username);
      
      // Execute the queue join operation with impersonated token
      await authService.executeAsImpersonatedUser(impersonationData.token, async () => {
        await joinQueue(charger.id, shift);
        // Set parking slot if provided
        if (parkingSlot?.trim()) {
          await chargerService.updateParkingSlot(charger.id, parkingSlot.trim());
        }
      });
      
      // Clean up impersonation data after successful operation
      delete window.__kioskImpersonationData;
      console.log('Kiosk mode: Queue joined successfully, cleaned up impersonation data');
    } else {
      // Normal user flow
      await joinQueue(charger.id, shift);
      // Set parking slot if provided
      if (parkingSlot?.trim()) {
        await chargerService.updateParkingSlot(charger.id, parkingSlot.trim());
      }
    }
    
    // Only close modal and refresh data on successful join
    setShowQueueModal(false);
    
    // Add small delay to ensure server has processed all updates before refreshing
    setTimeout(() => {
      onAction();
    }, 500);
  };

  const handleCancelQueue = () => {
    setShowQueueModal(true);
  };

  const handleLeaveQueue = async () => {
    try {
      setIsLoading(true);
      await chargerService.cancelQueue(charger.id);
      setShowQueueModal(false); // Close modal on successful leave
      onAction();
    } catch (error: any) {
      console.error('Failed to cancel queue:', error);
      
      // Error will be handled within the modal
      throw error; // Re-throw to let modal handle the error display
    } finally {
      setIsLoading(false);
    }
  };

  // Handle start charging button click with kiosk mode check
  const handleStartChargingClick = () => {
    setStartChargingError(null); // Clear any previous errors
    
    // Check if this is a force start scenario
    if (canForceStart) {
      setShowForceStartModal(true);
      return;
    }
    
    if (authService.isKioskUser()) {
      setKioskActionType('charging');
      setShowFindUserModal(true);
      return;
    }
    setShowStartModal(true);
  };

  // Handle user selection in kiosk mode
  const handleKioskUserSelect = async (selectedUser: any) => {
    try {
      setIsLoading(true);
      console.log('Kiosk mode: Impersonating user:', selectedUser);
      
      // Get impersonation token without switching the app context
      const { token: impersonationToken, user } = await authService.impersonate(selectedUser.telegramId);
      
      console.log('Impersonation successful for user:', user.username);
      console.log('Received impersonation token (not stored globally)');
      
      // Store the impersonation data temporarily for the selected action
      window.__kioskImpersonationData = {
        token: impersonationToken,
        user: user
      };
      
      // Close find user modal and open appropriate modal based on action type
      setShowFindUserModal(false);
      
      if (kioskActionType === 'charging') {
        setStartChargingError(null); // Clear any previous errors
        setShowStartModal(true);
      } else if (kioskActionType === 'queue') {
        setShowQueueModal(true);
      }
      
      // Reset action type
      setKioskActionType(null);
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      // Handle error - maybe show a toast or alert
      alert('Failed to select user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCharging = () => {
    setExtendChargingError(null); // Clear any previous errors
    setShowUpdateModal(true);
  };

  const handleStopCharging = async () => {
    try {
      setIsLoading(true);
      await chargerService.stopCharging(charger.id);
      setShowUpdateModal(false);
      onAction();
    } catch (error: any) {
      console.error('Failed to stop charging:', error);
      
      let errorMessage = 'Failed to stop charging. Please try again.';
      if (error.response?.data?.message || error.message) {
        errorMessage = error.response?.data?.message || error.message;
      }
      
      console.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExtendCharging = async (additionalMinutes: number) => {
    try {
      setIsLoading(true);
      setExtendChargingError(null); // Clear any previous errors
      await chargerService.extendCharging(charger.id, { additionalMinutes });
      setShowUpdateModal(false);
      onAction();
    } catch (error: any) {
      console.error('Failed to extend charging:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error response keys:', Object.keys(error.response?.data || {}));
      
      let errorMessage = t('errors.generic');
      
      // Check for localization keys from backend validation
      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;
        console.log('Server message:', serverMessage);
        if (serverMessage === 'extensionTimeExceedsMaximum') {
          errorMessage = t('errors.extensionTimeExceedsMaximum');
        } else if (serverMessage === 'chargingTimeExceedsMaximum') {
          errorMessage = t('errors.chargingTimeExceedsMaximum');
        } else if (serverMessage === 'invalidChargingDuration') {
          errorMessage = t('errors.invalidChargingDuration');
        } else {
          errorMessage = serverMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Set error state to display in modal
      setExtendChargingError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReduceCharging = async (minutesToReduce: number) => {
    try {
      setIsLoading(true);
      setExtendChargingError(null); // Clear any previous errors
      await chargerService.reduceCharging(charger.id, { minutesToReduce });
      setShowUpdateModal(false);
      onAction();
    } catch (error: any) {
      console.error('Failed to reduce charging:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error response keys:', Object.keys(error.response?.data || {}));
      
      let errorMessage = t('errors.generic');
      
      // Check for localization keys from backend validation
      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;
        console.log('Server message:', serverMessage);
        if (serverMessage === 'reductionTimeExceedsMaximum') {
          errorMessage = t('errors.reductionTimeExceedsMaximum');
        } else if (serverMessage === 'reductionBelowMinimumBuffer') {
          errorMessage = t('errors.reductionBelowMinimumBuffer');
        } else if (serverMessage === 'reductionBelowMinimumDuration') {
          errorMessage = t('errors.reductionBelowMinimumDuration');
        } else if (serverMessage === 'invalidReductionDuration') {
          errorMessage = t('errors.invalidReductionDuration');
        } else if (serverMessage === 'invalidChargingDuration') {
          errorMessage = t('errors.invalidChargingDuration');
        } else {
          errorMessage = serverMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Set error state to display in modal
      setExtendChargingError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleOnline = async () => {
    try {
      setIsLoading(true);
      await chargerService.updateStatus(charger.id, { 
        isOnline: !charger.online,
        reason: charger.online ? 'Marked offline by user' : 'Marked online by user'
      });
      onAction();
    } catch (error) {
      console.error('Failed to update charger status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {queueJoinModals}
      <div className="modern-charger-card" data-charger-id={charger.id}>
        {/* Header Section */}
        <div className="charger-card-header">
          <div className="charger-title-section">
            <div className="charger-icon-container" style={{ backgroundColor: statusInfo.bgColor }}>
              <StatusIcon size={20} style={{ color: statusInfo.color }} />
            </div>
            <div className="charger-title-info">
              <h3 className="charger-name">{charger.name}</h3>
              <p className="charger-slot">{t('home.charger.slot', { number: charger.slotNumber })}</p>
            </div>
          </div>
          
          <div className="charger-status-section">
            <div 
              className="status-indicator"
              style={{ 
                backgroundColor: statusInfo.bgColor,
                color: statusInfo.color 
              }}
            >
              {statusInfo.label}
            </div>
          </div>
        </div>

        {/* Details Section */}
        <div className="charger-details-section">
          {/* Show charging user information prominently */}
          {charger.status === 'charging' && charger.currentUser && (
            <div className="charging-user-info">
              <div className="charging-user-header">
                <Zap size={16} style={{ color: charger.isSessionOverdue ? 'var(--color-error)' : 'var(--color-charging)' }} />
                <span className="charging-label">
                  {charger.isSessionOverdue ? t('home.charger.overdue') : t('home.charger.charging')}
                </span>
                {charger.isSessionOverdue && (
                  <span className="overdue-warning">⚠️</span>
                )}
              </div>
              <div className="charging-user-details">
                <div className="user-name">{charger.currentUser.fullName}</div>
                <div className="user-details">
                  <a 
                    href={`tel:${charger.currentUser.maskedPhone.replace(/[^\d+]/g, '')}`}
                    className="phone-link inline"
                    title={`Call ${charger.currentUser.fullName}`}
                  >
                    {charger.currentUser.maskedPhone}
                  </a>
                  <span className="plate-separator">•</span>
                  <span>{charger.currentUser.primaryPlate}</span>
                </div>
              </div>
              {charger.estimatedEndTime && (
                <div className="availability-info">
                  <Clock size={14} style={{ color: charger.isSessionOverdue ? 'var(--color-error)' : 'var(--color-queue)' }} />
                  <span className="availability-text" style={{ color: charger.isSessionOverdue ? 'var(--color-error)' : 'inherit' }}>
                    {(() => {
                      const endTime = new Date(charger.estimatedEndTime);
                      const now = new Date();
                      const diffMinutes = Math.ceil((endTime.getTime() - now.getTime()) / (1000 * 60));
                      
                      if (charger.isSessionOverdue) {
                        const overdueMinutes = Math.abs(diffMinutes);
                        if (overdueMinutes < 60) {
                          return t('home.charger.overdueByMinutes', { minutes: overdueMinutes });
                        } else {
                          const hours = Math.floor(overdueMinutes / 60);
                          const mins = overdueMinutes % 60;
                          return t('home.charger.overdueByHours', { hours, minutes: mins });
                        }
                      } else if (diffMinutes <= 0) {
                        return t('home.charger.availableNow');
                      } else if (diffMinutes < 60) {
                        return t('home.charger.availableInMinutes', { minutes: diffMinutes });
                      } else {
                        const hours = Math.floor(diffMinutes / 60);
                        const mins = diffMinutes % 60;
                        return t('home.charger.availableInHours', { hours, minutes: mins });
                      }
                    })()}
                    {!charger.isSessionOverdue && (
                      <>
                        {' • '}
                        {new Date(charger.estimatedEndTime).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="detail-row">
            <div className="detail-item queue-info-clickable" onClick={handleToggleOnline}>
              <Wifi size={14} style={{ color: charger.online ? 'var(--color-available)' : 'var(--color-offline)' }} />
              <span className="detail-text">{charger.online ? t('home.charger.online') : t('home.charger.offline')}</span>
            </div>
            
            <div className="detail-item queue-info-clickable" onClick={() => setShowFindUserModal(true)}>
              <UserSearch size={14} style={{ color: 'var(--color-primary)' }} />
              <span className="detail-text">{t('home.charger.actions.findUser')}</span>
            </div>
            
            {queueLength > 0 && (
              <div className="detail-item queue-info-clickable" onClick={() => setShowQueueUsersModal(true)}>
                <Users size={14} style={{ color: 'var(--color-queue)' }} />
                <span className="detail-text">{t('home.charger.queue', { count: queueLength, max: charger.maxQueueLength })}</span>
              </div>
            )}

            {isUserInQueue && charger.userQueuePosition && (
              <div className="detail-item" style={{ color: 'var(--color-primary)' }}>
                <Calendar size={14} />
                <span className="detail-text">{t('session.queue.position', { position: charger.userQueuePosition, total: queueLength })}</span>
              </div>
            )}
          </div>
        </div>

        {/* Primary Action */}
        <div className="charger-primary-action">
          {canStartCharging && charger.status === 'idle' && (
            <button
              className="btn-modern btn-primary-modern"
              onClick={handleStartChargingClick}
              disabled={isLoading}
              data-action="start-charging"
            >
              <Play size={16} />
              {t('home.charger.actions.startCharging')}
            </button>
          )}

          {canStartCharging && charger.status === 'awaitingNext' && isFirstRealUserInQueue && (
            <button
              className="btn-modern btn-primary-modern btn-urgent"
              onClick={handleStartChargingClick}
              disabled={isLoading}
              data-action="accept-your-turn"
            >
              <Play size={16} />
              {t('home.charger.actions.acceptYourTurn', 'Accept Your Turn!')}
            </button>
          )}

          {/* Force start button for next user in queue when someone is charging */}
          {canForceStart && (
            <button
              className="btn-modern btn-warning-modern"
              onClick={handleStartChargingClick}
              disabled={isLoading}
              data-action="force-start-charging"
              title={t('home.charger.actions.forceStartTooltip', 'Force Start Charging (Take Over)')}
            >
              <Play size={16} />
              {t('home.charger.actions.forceStart', 'Force Start')}
            </button>
          )}

          {showUpdateCharging && (
            <button
              className="btn-modern btn-primary-modern"
              onClick={handleUpdateCharging}
              disabled={isLoading}
            >
              <Zap size={16} />
              {t('session.actions.updateSession')}
            </button>
          )}

          {canJoinQueue && (
            <button
              className="btn-modern btn-secondary-action-modern"
              onClick={handleJoinQueue}
              disabled={isLoading}
            >
              <Users size={16} />
              {t('home.charger.actions.joinQueue')} ({queueLength}/{charger.maxQueueLength})
            </button>
          )}

          {showCancelQueue && (
            <button
              className="btn-modern btn-warning-modern"
              onClick={handleCancelQueue}
              disabled={isLoading}
            >
              <Users size={16} />
              {t('home.charger.actions.manageQueue')} ({t('session.queue.position', { position: charger.userQueuePosition, total: queueLength })})
            </button>
          )}

          {isUserChargingAnywhere && !isUserCharging && (
            <button className="btn-modern btn-disabled-modern" disabled>
              <Zap size={16} />
              {t('home.charger.chargingElsewhere')}
            </button>
          )}

          {!canStartCharging && !canJoinQueue && !showCancelQueue && !showUpdateCharging && !isUserChargingAnywhere && charger.online && queueLength >= charger.maxQueueLength && (
            <button className="btn-modern btn-disabled-modern" disabled>
              <Users size={16} />
              {t('home.charger.queueFull')} ({queueLength}/{charger.maxQueueLength})
            </button>
          )}

          {/* Special handling for awaitingNext status when user is not first in queue */}
          {charger.status === 'awaitingNext' && isUserInQueue && !isFirstRealUserInQueue && (
            <button className="btn-modern btn-disabled-modern" disabled>
              <Clock size={16} />
              {t('home.charger.awaitingNextUser', 'Awaiting Next User')} (#{charger.userQueuePosition})
            </button>
          )}

          {charger.status === 'awaitingNext' && !isUserInQueue && (
            <button className="btn-modern btn-disabled-modern" disabled>
              <Clock size={16} />
              {t('home.charger.awaitingNextUser', 'Awaiting Next User')}
            </button>
          )}

          {!canStartCharging && !canJoinQueue && !isUserInQueue && !showUpdateCharging && !charger.online && (
            <button className="btn-modern btn-disabled-modern" disabled>
              <WifiOff size={16} />
              {t('home.charger.offline')}
            </button>
          )}
        </div>

      </div>

      {showStartModal && (
        <StartChargingModal
          chargerName={charger.name}
          onStart={handleStartCharging}
          onCancel={() => {
            setShowStartModal(false);
            setStartChargingError(null); // Clear error when modal is closed
            // Clean up impersonation data if user cancels
            if (authService.isKioskUser() && window.__kioskImpersonationData) {
              delete window.__kioskImpersonationData;
              console.log('Kiosk mode: Cleaned up impersonation data after cancel');
            }
          }}
          isLoading={isLoading}
          error={startChargingError}
        />
      )}

      {showForceStartModal && (
        <StartChargingModal
          chargerName={charger.name}
          onStart={handleForceStart}
          onCancel={() => {
            setShowForceStartModal(false);
            setStartChargingError(null);
          }}
          isLoading={isLoading}
          error={startChargingError}
          isForceStart={true}
        />
      )}

      {showUpdateModal && (
        <UpdateChargingModal
          chargerName={charger.name}
          estimatedEndTime={charger.estimatedEndTime}
          maxEndTime={charger.maxEndTime}
          isOverdue={charger.isSessionOverdue}
          startedAt={charger.startedAt}
          onStop={handleStopCharging}
          onExtend={handleExtendCharging}
          onReduce={handleReduceCharging}
          onCancel={() => setShowUpdateModal(false)}
          isLoading={isLoading}
          error={extendChargingError}
        />
      )}

      {showQueueModal && (
        <QueueModal
          charger={charger}
          onJoin={async (shift: number, parkingSlot?: string) => {
            try {
              setIsLoading(true);
              await handleConfirmJoinQueue(shift, parkingSlot);
              setShowQueueModal(false); // Only close on successful join
            } catch (error) {
              // Error is handled within the modal, don't close modal
              throw error; // Re-throw so modal can handle it
            } finally {
              setIsLoading(false);
            }
          }}
          onLeave={async () => {
            try {
              setIsLoading(true);
              await handleLeaveQueue();
              setShowQueueModal(false); // Close on successful leave
            } catch (error) {
              // Error is handled within the modal, don't close modal
              throw error; // Re-throw so modal can handle it
            } finally {
              setIsLoading(false);
            }
          }}
          onCancel={() => setShowQueueModal(false)}
          isLoading={isLoading}
        />
      )}

      {/* ManageQueueModal removed, unified with QueueModal */}

      {showFindUserModal && (
        <FindUserModal
          isOpen={showFindUserModal}
          onClose={() => {
            setShowFindUserModal(false);
            setKioskActionType(null);
          }}
          onUserSelect={handleKioskUserSelect}
          chargerName={charger.name}
        />
      )}

      {showQueueUsersModal && (
        <QueueUsersModal
          charger={charger}
          onClose={() => setShowQueueUsersModal(false)}
        />
      )}
    </>
  );
}

interface StartChargingModalProps {
  chargerName: string;
  onStart: (minutes: number) => void;
  onCancel: () => void;
  isLoading: boolean;
  error?: string | null;
  isForceStart?: boolean; // New parameter to show warning and change behavior
}

function StartChargingModal({ chargerName, onStart, onCancel, isLoading, error, isForceStart = false }: StartChargingModalProps) {
  const { t } = useTranslation();
  const [selectedMinutes, setSelectedMinutes] = useState(90);
  const [customMinutes, setCustomMinutes] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  const presets = [
    { minutes: 60, label: t('dialogs.startCharging.presets.60', '1 hour'), subtitle: t('dialogs.startCharging.quickCharge', 'Quick charge') },
    { minutes: 90, label: t('dialogs.startCharging.presets.90', '1.5 hours'), subtitle: t('dialogs.startCharging.standard', 'Standard') },
    { minutes: 120, label: t('dialogs.startCharging.presets.120', '2 hours'), subtitle: t('dialogs.startCharging.extended', 'Extended') },
    { minutes: 180, label: t('dialogs.startCharging.presets.180', '3 hours'), subtitle: t('dialogs.startCharging.longCharge', 'Long charge') }
  ];

  const handleStart = () => {
    const minutes = useCustom ? parseInt(customMinutes) || 90 : selectedMinutes;
    onStart(minutes);
  };

  return (
    <div className="modal-overlay-modern">
      <div className="modal-modern">
        <div className="modal-header-modern">
          <h3 className="modal-title">
            {isForceStart 
              ? t('dialogs.forceStart.title', 'Confirm Force Start')
              : t('dialogs.startCharging.title', 'Start Charging Session')
            }
          </h3>
          <p className="modal-subtitle">{chargerName}</p>
        </div>
        
        <div className="modal-content-modern">
          {/* Force Start Warning - only shown when isForceStart is true */}
          {isForceStart && (
            <div className="force-start-warning">
              <div className="warning-alert">
                <span>⚠️</span>
                <div>
                  <p>{t('dialogs.forceStart.warningMessage', 'You are about to start charging which will automatically end the current charging session.')}</p>
                  <p>{t('dialogs.forceStart.confirmationQuestion', 'Are you sure the charger is really empty and available for you to use?')}</p>
                </div>
              </div>
            </div>
          )}
          
          <p className="modal-description">{t('dialogs.startCharging.estimatedTime', 'Estimated charging time (minutes)')}</p>
          
          {error && (
            <div className="queue-error-message">
              <div className="error-alert">
                <X size={16} />
                <span>{error}</span>
              </div>
            </div>
          )}
          
          <div className="time-presets-modern">
            {presets.map(({ minutes, label, subtitle }) => (
              <button
                key={minutes}
                className={`preset-btn-modern ${!useCustom && selectedMinutes === minutes ? 'preset-btn-active-modern' : ''}`}
                onClick={() => {
                  setSelectedMinutes(minutes);
                  setUseCustom(false);
                }}
              >
                <div className="preset-label">{label}</div>
                <div className="preset-subtitle">{subtitle}</div>
              </button>
            ))}
          </div>

          <div className="custom-time-modern">
            <button
              className={`custom-option-modern ${useCustom ? 'custom-option-active-modern' : ''}`}
              onClick={() => setUseCustom(true)}
            >
              <div className="custom-option-content">
                <span className="custom-option-label">{t('dialogs.startCharging.custom')}</span>
                {useCustom && (
                  <input
                    type="number"
                    value={customMinutes}
                    onChange={(e) => setCustomMinutes(e.target.value)}
                    placeholder={t('dialogs.startCharging.minutesPlaceholder')}
                    min="1"
                    max="240"
                    className="custom-input-modern"
                    autoFocus
                  />
                )}
              </div>
            </button>
          </div>
        </div>

        <div className="modal-actions-modern">
          <button
            className="btn-modern btn-secondary-modern"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </button>
          <button
            className="btn-modern btn-primary-modern"
            onClick={handleStart}
            disabled={isLoading}
          >
            <Zap size={16} />
            {isLoading 
              ? t('common.loading', 'Loading...') 
              : isForceStart 
                ? t('dialogs.forceStart.confirmButton', 'Yes, Start Charging')
                : t('dialogs.startCharging.start', 'Start Charging')
            }
          </button>
        </div>
      </div>
    </div>
  );
}

interface QueueModalProps {
  charger: Charger;
  onJoin: (shift: number, parkingSlot?: string) => Promise<void>;
  onLeave?: () => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function QueueModal({ charger, onJoin, onLeave, onCancel, isLoading }: QueueModalProps) {
  const { t } = useTranslation();
  const [queueDetails, setQueueDetails] = useState<QueueDetail | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedShift, setSelectedShift] = useState<number | null>(null);
  const [parkingSlot, setParkingSlot] = useState<string>('');
  const [access, setAccess] = useState<QueueAccessCheck | null>(null);
  
  // Check if user is already in queue
  const isUserInQueue = charger.isUserInQueue;
  const userQueuePosition = charger.userQueuePosition;
  
  console.log('QueueModal init - charger:', charger);
  console.log('QueueModal init - isUserInQueue:', isUserInQueue);
  console.log('QueueModal init - userQueuePosition:', userQueuePosition);

  const handleShiftSelection = (shift: number) => {
    setSelectedShift(shift);
    setErrorMessage(null); // Clear any existing errors when selecting a new shift
  };

  const handleLeaveQueue = async () => {
    if (!onLeave) return;
    
    try {
      setLoadingDetails(true);
      setErrorMessage(null);
      await onLeave();
    } catch (error: any) {
      console.error('Failed to leave queue:', error);
      
      let displayError = t('errors.generic');
      if (error.response?.data?.message) {
        displayError = error.response.data.message;
      } else if (error.response?.data?.error) {
        displayError = error.response.data.error;
      } else if (error.message) {
        displayError = error.message;
      }
      
      setErrorMessage(displayError);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleUpdateQueue = async () => {
    if (!selectedShift) return;
    
    try {
      setLoadingDetails(true);
      setErrorMessage(null);
      
      // Preserve the current parking slot value (may be empty, which is valid)
      const currentParkingSlot = parkingSlot;
      
      console.log('Update Queue: Step 1 - Preserving parking slot:', currentParkingSlot);
      
      // Step 1: Leave the current queue position (this clears parking slot on server)
      console.log('Update Queue: Step 2 - Leaving current queue');
      await chargerService.cancelQueue(charger.id);
      
      // Step 2 & 3: Use onJoin to handle joining + parking slot update with proper parent refresh
      console.log('Update Queue: Step 3 - Calling onJoin with shift and parking slot');
      await onJoin(selectedShift, currentParkingSlot);
      
      console.log('Update Queue: Complete - Modal will be closed by onJoin');
    } catch (error: any) {
      console.error('Failed to update queue:', error);
      
      // Handle error locally within the modal
      let displayError = t('errors.generic');
      
      // First priority: Use the server's localized error message if available
      if (error.response?.data?.message) {
        displayError = error.response.data.message;
      } else if (error.response?.data?.error) {
        displayError = error.response.data.error;
      } else if (error.message) {
        // Fallback to error.message if no structured response
        if (error.message.includes('active charging session') || error.message.includes('הפעלת טעינה פעילה') || error.message.includes('активной сессии зарядки')) {
          displayError = t('errors.queueJoinActiveSession');
        } else if (error.message.includes('already in a queue') || error.message.includes('כבר נמצא בתור') || error.message.includes('уже находitesь в очереди')) {
          displayError = t('errors.queueJoinAlreadyInQueue');
        } else {
          displayError = error.message;
        }
      }
      
      setErrorMessage(displayError);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleJoinQueue = async () => {
    if (!selectedShift) return;
    
    try {
      setLoadingDetails(true);
      setErrorMessage(null); // Clear any existing errors
      await onJoin(selectedShift, parkingSlot);
      // Modal will be closed by parent component after successful join
    } catch (error: any) {
      console.error('Failed to join queue:', error);
      
      // Handle error locally within the modal
      let displayError = t('errors.generic');
      
      // First priority: Use the server's localized error message if available
      if (error.response?.data?.message) {
        displayError = error.response.data.message;
      } else if (error.response?.data?.error) {
        displayError = error.response.data.error;
      } else if (error.message) {
        // Fallback to error.message if no structured response
        if (error.message.includes('active charging session') || error.message.includes('הפעלת טעינה פעילה') || error.message.includes('активной сессии зарядки')) {
          displayError = t('errors.queueJoinActiveSession');
        } else if (error.message.includes('already in a queue') || error.message.includes('כבר נמצא בתור') || error.message.includes('уже находitesь в очереди')) {
          displayError = t('errors.queueJoinAlreadyInQueue');
        } else {
          displayError = error.message;
        }
      }
      
      setErrorMessage(displayError);
      setLoadingDetails(false);
    }
  };

  const loadQueueDetails = async () => {
    try {
      setLoadingDetails(true);
      const details = await chargerService.getChargerQueue(charger.id);
      setQueueDetails(details);
      
      console.log('LoadQueueDetails - charger.isUserInQueue:', isUserInQueue);
      console.log('LoadQueueDetails - charger.userQueuePosition:', userQueuePosition);
      console.log('LoadQueueDetails - queue details:', details);
      
      // If user is in queue according to charger prop, pre-select their shift and parking slot
      if (isUserInQueue && userQueuePosition) {
        setSelectedShift(userQueuePosition);
        
        // Find user's parking slot from queue details
        const userInQueue = details.queueUsers.find(u => u.position === userQueuePosition);
        console.log('LoadQueueDetails - userInQueue found:', userInQueue);
        
        // Always set parking slot state, even if empty (to ensure state reflects server data)
        const parkingSlotToSet = userInQueue?.parkingSlot || '';
        console.log('LoadQueueDetails - setting parking slot to:', parkingSlotToSet);
        setParkingSlot(parkingSlotToSet);
      } else {
        console.log('LoadQueueDetails - user not in queue according to charger prop');
        console.log('LoadQueueDetails - isUserInQueue:', isUserInQueue, 'userQueuePosition:', userQueuePosition);
      }
    } catch (error) {
      console.error('Failed to load queue details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    loadQueueDetails();
  }, [charger.id]);

  // Join rules for this user right now (opening window, whether shift 1 is possible)
  useEffect(() => {
    queueAccessService.check().then(setAccess).catch(() => setAccess(null));
  }, []);

  // Web users can never hold shift 1; Telegram users need to be at MATAF
  // Web users: shift 1 opens at a set time (the server says when via minShift)
  const shift1Locked = !!access && !access.isTelegramUser && access.minShift > 1;
  // Charged yesterday: only shift 3 today
  const chargedYesterdayMinShift = access?.chargedYesterday ? access.minShift : 1;

  return (
    <div className="modal-overlay-modern">
      <div className="modal-modern queue-modal">
        <div className="modal-header-modern">
          <h3 className="modal-title">
            {isUserInQueue ? t('dialogs.manageQueue.title') : t('dialogs.joinQueue.title')}
          </h3>
          <p className="modal-subtitle">{charger.name} - {t('home.charger.slot', { number: charger.slotNumber })}</p>
          <button
            className="modal-close-btn"
            onClick={onCancel}
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="modal-content-modern">
          {loadingDetails ? (
            <div className="queue-loading">
              <Clock className="animate-spin" size={24} />
              <p>{t('common.loading')}</p>
            </div>
          ) : queueDetails ? (
            <>
              <div className="queue-status-section">
                <div className="queue-current-status">
                  <h4>{t('dialogs.manageQueue.parkingSlot.title')}</h4>
                  <div className="current-user-info">
                    <Car size={16} style={{ color: 'var(--color-primary)' }} />
                    <input
                      type="text"
                      value={parkingSlot}
                      onChange={(e) => setParkingSlot(e.target.value)}
                      placeholder={t('dialogs.manageQueue.parkingSlot.placeholder')}
                      className="parking-slot-input-inline"
                      maxLength={10}
                    />
                  </div>
                  <p className="parking-slot-hint">{t('dialogs.manageQueue.parkingSlot.description', 'Optional parking slot information')}</p>
                </div>
              </div>

              {/* Always show shift selection section with same content structure */}
              <div className="shift-selection-section">
                <h4>{t('dialogs.joinQueue.selectShift')}</h4>
                <p className="shift-description">{t('dialogs.joinQueue.shiftDescription')}</p>
                
                <div className="shift-options">
                    {Array.from({ length: queueDetails.maxQueueLength }, (_, index) => {
                      const shiftNumber = index + 1;
                      const existingUser = queueDetails.queueUsers.find(u => u.position === shiftNumber);
                      const isOccupied = !!existingUser;
                      const isLockedAfterCharging = shiftNumber < chargedYesterdayMinShift && !isOccupied;
                      const isLocked = (shiftNumber === 1 && shift1Locked && !isOccupied) || isLockedAfterCharging;
                      const isAvailable = !isOccupied && !isLocked;
                      
                      return (
                        <button
                          key={shiftNumber}
                          className={`shift-option ${isOccupied || isLocked ? 'shift-occupied' : selectedShift === shiftNumber ? 'shift-selected' : 'shift-available'}`}
                          onClick={() => isAvailable ? handleShiftSelection(shiftNumber) : null}
                          disabled={!isAvailable || loadingDetails}
                        >
                          <div className="shift-number">
                            <div className="shift-label">{t('dialogs.joinQueue.shift')} #{shiftNumber}</div>
                            <div className="shift-priority">
                              {shiftNumber === 1 ? t('dialogs.joinQueue.priority.highest') : 
                               shiftNumber === queueDetails.maxQueueLength ? t('dialogs.joinQueue.priority.lowest') : 
                               t('dialogs.joinQueue.priority.medium')}
                            </div>
                          </div>
                          
                          <div className="shift-details">
                            {isOccupied && existingUser ? (
                              <>
                                <div className="shift-user-name">{existingUser.fullName}</div>
                                <div className="shift-user-details">
                                  <a 
                                    href={`tel:${existingUser.maskedPhone.replace(/[^\d+]/g, '')}`}
                                    className="phone-link inline"
                                    title={`Call ${existingUser.fullName}`}
                                  >
                                    {existingUser.maskedPhone}
                                  </a> • {existingUser.primaryPlate}
                                </div>
                                {existingUser.estimatedStartTime && (
                                  <div className="shift-estimated-start">
                                    {t('dialogs.joinQueue.estimatedStart')}: {new Date(existingUser.estimatedStartTime).toLocaleTimeString()}
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <div className="shift-status">
                                  {isLockedAfterCharging
                                    ? t('queueAccess.chargedYesterdayLocked')
                                    : isLocked
                                      ? t('queueAccess.shift1WebFrom', { time: access?.webShift1From ?? '07:00' })
                                      : t('home.charger.status.available')}
                                </div>
                                {shiftNumber === 1 && access?.isTelegramUser && access.locationCheckEnabled && (
                                  <div className="shift-access-hint">{t('queueAccess.shift1NeedsLocation')}</div>
                                )}
                                <div className="shift-estimate">
                                  {t('dialogs.joinQueue.estimatedWait')}: ~{(shiftNumber - 1) * 60} {t('time.min')}
                                </div>
                              </>
                            )}
                          </div>
                          
                          {isAvailable && (
                            <div className="shift-select-indicator">
                              {selectedShift === shiftNumber ? (
                                <div className="shift-selected-check">✓</div>
                              ) : (
                                <Circle size={20} />
                              )}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

              <div className="queue-status-section">
                <div className="queue-current-status">
                  <h4>{t('dialogs.joinQueue.currentStatus')}</h4>
                  {queueDetails.currentUser ? (
                    <div className="current-user-info">
                      <Zap size={16} style={{ color: 'var(--color-charging)' }} />
                      <span>{t('dialogs.joinQueue.charging')}: {queueDetails.currentUser.fullName}</span>
                      {queueDetails.estimatedEndTime && (
                        <span className="estimated-end">
                          {t('dialogs.joinQueue.until')} {new Date(queueDetails.estimatedEndTime).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="charger-idle">
                      <Zap size={16} style={{ color: 'var(--color-available)' }} />
                      <span>{t('home.charger.status.available')}</span>
                    </div>
                  )}
                </div>
              </div>

              {errorMessage && (
                <div className="queue-error-message">
                  <div className="error-alert">
                    <X size={16} />
                    <span>{errorMessage}</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="queue-error">
              <p>{t('errors.failedToLoad')}</p>
            </div>
          )}
        </div>

        <div className="modal-actions-modern">
          <button
            className="btn-modern btn-secondary-modern"
            onClick={onCancel}
            disabled={isLoading || loadingDetails}
          >
            {t('common.cancel')}
          </button>
          
          {isUserInQueue ? (
            // User is in queue - show Update Queue and Leave Queue buttons
            <>
              <button
                className="btn-modern btn-danger-modern"
                onClick={handleLeaveQueue}
                disabled={isLoading || loadingDetails}
              >
                <X size={16} />
                {loadingDetails ? t('common.loading') : t('home.charger.actions.leaveQueue')}
              </button>
              <button
                className="btn-modern btn-primary-modern"
                onClick={handleUpdateQueue}
                disabled={isLoading || loadingDetails || !selectedShift}
              >
                <Users size={16} />
                {loadingDetails ? t('common.loading') : t('dialogs.manageQueue.actions.updateQueue')}
              </button>
            </>
          ) : (
            // User is not in queue - show Join Queue button
            <button
              className="btn-modern btn-primary-modern"
              onClick={handleJoinQueue}
              disabled={isLoading || loadingDetails || !selectedShift}
            >
              <Users size={16} />
              {loadingDetails ? t('common.loading') : t('home.charger.actions.joinQueue')}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}

interface QueueUsersModalProps {
  charger: Charger;
  onClose: () => void;
}

function QueueUsersModal({ charger, onClose }: QueueUsersModalProps) {
  const { t } = useTranslation();
  const [queueDetails, setQueueDetails] = useState<QueueDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadQueueDetails = async () => {
      try {
        setLoading(true);
        const details = await chargerService.getChargerQueue(charger.id);
        setQueueDetails(details);
      } catch (error) {
        console.error('Failed to load queue details:', error);
      } finally {
        setLoading(false);
      }
    };

    loadQueueDetails();
  }, [charger.id]);

  return (
    <div className="modal-overlay-modern">
      <div className="modal-modern queue-users-modal">
        <div className="modal-header-modern">
          <h3 className="modal-title">{t('home.charger.peopleInQueue')}</h3>
          <p className="modal-subtitle">{charger.name} - {t('home.charger.slot', { number: charger.slotNumber })}</p>
          <button
            className="modal-close-btn"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="modal-content-modern">
          {loading ? (
            <div className="queue-loading">
              <Clock className="animate-spin" size={24} />
              <p>{t('common.loading')}</p>
            </div>
          ) : queueDetails ? (
            <>
              {/* Current User Status */}
              <div className="queue-status-section">
                <div className="queue-current-status">
                  <h4>{t('dialogs.joinQueue.currentStatus')}</h4>
                  {queueDetails.currentUser ? (
                    <div className="current-user-info">
                      <Zap size={16} style={{ color: 'var(--color-charging)' }} />
                      <span>{t('dialogs.joinQueue.charging')}: {queueDetails.currentUser.fullName}</span>
                      {queueDetails.estimatedEndTime && (
                        <span className="estimated-end">
                          {t('dialogs.joinQueue.until')} {new Date(queueDetails.estimatedEndTime).toLocaleTimeString()}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="charger-idle">
                      <Zap size={16} style={{ color: 'var(--color-available)' }} />
                      <span>{t('home.charger.status.available')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Queue Users List */}
              <div className="queue-users-section">
                <h4>{t('dialogs.queueUsers.usersInQueue')} ({queueDetails.queueUsers.length}/{queueDetails.maxQueueLength})</h4>
                {queueDetails.queueUsers.length > 0 ? (
                  <div className="queue-users-list">
                    {queueDetails.queueUsers.map((user, index) => (
                      <div key={index} className={`queue-user-item ${user.isCurrentUser ? 'current-user' : ''}`}>
                        <div className="queue-user-position">
                          <span className="position-number">#{user.position}</span>
                        </div>
                        <div className="queue-user-info">
                          <div className="queue-user-name">
                            {user.fullName}
                            {user.isCurrentUser && <span className="you-indicator"> ({t('dialogs.queueUsers.you')})</span>}
                          </div>
                          <div className="queue-user-details">
                            <div className="detail-item">
                              <span className="detail-text">📞 </span>
                              <a 
                                href={`tel:${user.maskedPhone.replace(/[^\d+]/g, '')}`}
                                className="phone-link inline"
                                title={`Call ${user.fullName}`}
                              >
                                {user.maskedPhone}
                              </a>
                            </div>
                            <div className="detail-item">
                              <span className="detail-text">🚗 {user.primaryPlate}</span>
                            </div>
                            {user.parkingSlot && (
                              <div className="detail-item">
                                <span className="detail-text">🅿️ {user.parkingSlot}</span>
                              </div>
                            )}
                          </div>
                          <div className="queue-user-timing">
                            <span className="joined-time">
                              {t('dialogs.queueUsers.joinedAt')}: {new Date(user.joinedAt).toLocaleTimeString()}
                            </span>
                            {user.estimatedStartTime && (
                              <span className="estimated-start">
                                {t('dialogs.queueUsers.estimatedStart')}: {new Date(user.estimatedStartTime).toLocaleTimeString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="queue-empty">
                    <Users size={24} style={{ color: 'var(--color-text-secondary)' }} />
                    <p>{t('dialogs.queueUsers.noUsersInQueue')}</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="queue-error">
              <p>{t('errors.failedToLoad')}</p>
            </div>
          )}
        </div>

        <div className="modal-actions-modern">
          <button
            className="btn-modern btn-secondary-modern"
            onClick={onClose}
          >
            {t('common.close')}
          </button>
        </div>

      </div>
    </div>
  );
}